import {
  ProductStatusEnum,
  ProductTypeName,
  ProductMarginRuleType,
  ProductTypePropertySlugEnum,
  RelatedProductCategoryNameEnum,
} from '@electricjs/core_entity-client';

import { ButtonIntent } from '@electricjs/arc/components/Button';

export const productStatusDisplayName: Record<ProductStatusEnum, string> = {
  [ProductStatusEnum.Active]: 'Active',
  [ProductStatusEnum.Inactive]: 'Inactive',
  [ProductStatusEnum.Pending]: 'Needs review',
  [ProductStatusEnum.OutOfStock]: 'Out of stock',
  [ProductStatusEnum.Discarded]: 'Discarded',
};

export const productStatusDisplayIntent: Record<
  ProductStatusEnum,
  ButtonIntent
> = {
  [ProductStatusEnum.Active]: 'success',
  [ProductStatusEnum.Inactive]: 'info',
  [ProductStatusEnum.Pending]: 'warning',
  [ProductStatusEnum.OutOfStock]: 'info',
  [ProductStatusEnum.Discarded]: 'info',
};

export const marginRuleTypeDisplayName: Record<ProductMarginRuleType, string> =
  {
    [ProductMarginRuleType.ByProduct]: 'Product specific',
    [ProductMarginRuleType.ByProductTypeAndManufacturer]:
      'By product type and manufacturer',
    [ProductMarginRuleType.ByManufacturer]: 'By manufacturer',
    [ProductMarginRuleType.ByProductType]: 'By product type',
    [ProductMarginRuleType.Default]: 'Default',
  };

export const getDynamicFieldsHelperText = (
  slug?: ProductTypePropertySlugEnum
) => {
  switch (slug) {
    case ProductTypePropertySlugEnum.SsdStorage:
      return 'Enter value in GB. 1 TB is 1000 GB. Values over 1000 GB will automatically display as TB within the storefront.';

    default:
      return undefined;
  }
};

export const getProductNamePlaceholder = (typeName?: ProductTypeName) => {
  switch (typeName) {
    case 'laptop':
      return 'e.g ex: Apple Macbook Pro 14" - M4 Max 14C32C - 36GB RAM - 1TB SSD - Silver';
    case 'monitor':
      return 'e.g Dell E2425HS 24" LED 1080p Monitor - 60Hz - Black';
    case 'keyboard':
      return 'e.g Apple Magic USB-C Keyboard - Wireless - US Layout - White';
    case 'mouse':
      return 'e.g Logitech Lift Vertical Ergonomic Mouse - Wireless - 1000-2400 DPI - Black';
    case 'dock':
      return 'e.g Plugable 13-in-1 USB-C Triple Monitor Docking Station - 4K@60Hz - 100W PD - Black';
    case 'audio':
      return 'e.g Logitech H111 Over-Ear Headset - Wired - Noise Reduction - Black';
    case 'microphone':
      return 'e.g Logitech G Yeti Orb Condenser Microphone - Wired - Gaming with LIGHTSYNC - Black';
    case 'headphones':
      return 'e.g Logitech H111 Over-Ear Headset - Wired - Noise Reduction - Black';
    case 'video':
      return 'e.g Adesso CyberTrack H5 1080p Webcam - USB 2.0 - Dual Mic - Black';
    case 'cable':
      return "e.g 4' HDMI to HDMI Cable - 4K@60Hz - Gold-Plated - Black";
    case 'power_adapter':
      return 'e.g Apple 96W USB-C Power Adapter - MacBook Compatible - White';
    case 'warranty':
      return 'e.g Dell 3 Years Warranty';
    case 'miscellaneous':
    default:
      return 'e.g Logitech Wireless Presenter - 100ft Range - USB-A Receiver - Black';
  }
};

export const getProductNameHelperText = (typeName?: ProductTypeName) => {
  switch (typeName) {
    case 'laptop':
      return 'Template: [Brand] [Model] [Screen size]” - [Processor] - [RAM]GB RAM - [SSD]GB SSD - [Color]';
    case 'monitor':
      return 'Template: [Brand] [Model] [Size]" [Panel Type] [Resolution] Monitor - [Refresh Rate] - [Color]';
    case 'keyboard':
      return 'Template: [Brand] [Model] [Type] Keyboard - [Connectivity] - [Layout] - [Color]';
    case 'mouse':
      return 'Template: [Brand] [Model] [Type] Mouse - [Connectivity] - [DPI Range] - [Color]';
    case 'dock':
      return 'Template: [Brand] [Model] [Number]-in-[Number] [Connection Type] Docking Station - [Display Support] - [Power Delivery] - [Color]';
    case 'audio':
      return 'Template: [Brand] [Model] [Type] - [Connectivity] - [Special Features] - [Color]';
    case 'microphone':
      return 'Template: [Brand] [Model] Microphone - [Connectivity] - [Special Features] - [Color]';
    case 'headphones':
      return 'Template: [Brand] [Model] Headphone - [Connectivity] - [Special Features] - [Color]';
    case 'video':
      return 'Template: [Brand] [Model] [Resolution] [Type] - [Connectivity] - [Special Features] - [Color]';
    case 'cable':
      return 'Template: [Length] [Type] to [Type] Cable - [Speed/Standard] - [Special Features] - [Color]';
    case 'power_adapter':
      return 'Template: [Brand] [Wattage]W [Type] Power Adapter - [Device Compatibility] - [Color]';
    case 'warranty':
      return '[Brand] [Period] [Year/Years] Warranty';
    case 'miscellaneous':
    default:
      return 'Template:[Brand] [Model] [Product Type] - [Key Feature] - [Compatibility] - [Color]';
  }
};

export const getRelatedProductsSectionTitle = (
  category?: RelatedProductCategoryNameEnum
) => {
  switch (category) {
    case RelatedProductCategoryNameEnum.Warranties:
      return 'Warranties';
    case RelatedProductCategoryNameEnum.Accessories:
      return 'Accessories';

    default:
      return '';
  }
};

export const getRelatedProductsSectionLinkButtonLabel = (
  category?: RelatedProductCategoryNameEnum
) => {
  switch (category) {
    case RelatedProductCategoryNameEnum.Warranties:
      return 'Link warranty';
    case RelatedProductCategoryNameEnum.Accessories:
      return 'Link accessory';

    default:
      return '';
  }
};

export const getRelatedProductsSectionEmptyMessage = (
  category?: RelatedProductCategoryNameEnum
) => {
  switch (category) {
    case RelatedProductCategoryNameEnum.Warranties:
      return 'No warranties';
    case RelatedProductCategoryNameEnum.Accessories:
      return 'No accessories';

    default:
      return '';
  }
};

export const getUnlinkProductModalTitle = (
  category?: RelatedProductCategoryNameEnum
) => {
  switch (category) {
    case RelatedProductCategoryNameEnum.Warranties:
      return 'Unlink warranty?';
    case RelatedProductCategoryNameEnum.Accessories:
      return 'Unlink accessory?';

    default:
      return '';
  }
};

export const getUnlinkProductModalMessage = (
  category?: RelatedProductCategoryNameEnum
) => {
  switch (category) {
    case RelatedProductCategoryNameEnum.Warranties:
      return 'Are you sure you want to unlink this warranty?';
    case RelatedProductCategoryNameEnum.Accessories:
      return 'Are you sure you want to unlink this accessory?';

    default:
      return '';
  }
};

export const getUnlinkProductSuccessMessage = (
  category?: RelatedProductCategoryNameEnum
) => {
  switch (category) {
    case RelatedProductCategoryNameEnum.Warranties:
      return 'Warranty successfully unlinked.';
    case RelatedProductCategoryNameEnum.Accessories:
      return 'Accessory successfully unlinked.';

    default:
      return '';
  }
};

export const getLinkProductSuccessMessage = (
  category?: RelatedProductCategoryNameEnum
) => {
  switch (category) {
    case RelatedProductCategoryNameEnum.Warranties:
      return 'Warranty has been successfully linked.';
    case RelatedProductCategoryNameEnum.Accessories:
      return 'Accessory has been successfully linked.';

    default:
      return '';
  }
};

export const getLinkProductFieldLabel = (
  category?: RelatedProductCategoryNameEnum
) => {
  switch (category) {
    case RelatedProductCategoryNameEnum.Warranties:
      return 'Warranty';
    case RelatedProductCategoryNameEnum.Accessories:
      return 'Accessory';

    default:
      return '';
  }
};
